import { SupabaseClient } from '@supabase/supabase-js';
import camelCase from 'camelcase-keys';
import snakeCase from 'snakecase-keys';
import z from 'zod';

import { Database, DisplaySymbol, DisplayUnit } from '@core';

export type CustomCorridor = {
    id: number;
    layoutId: number;
    innerMax: number;
    innerMin: number;
    swingMeasuredParameterId: number;
};

export type DisplayCustomCorridor = CustomCorridor & {
    displayUnit: DisplayUnit;
    displaySymbol: DisplaySymbol;
};

export type CustomCorridorPayload = Omit<CustomCorridor, 'id'>;

const customCorridorSchema = z.object({
    id: z.number(),
    layoutId: z.number(),
    innerMin: z.number(),
    innerMax: z.number(),
    swingMeasuredParameterId: z.number(),
});

export async function upsertCustomCorridor(
    supabase: SupabaseClient<Database>,
    customCorridor: Partial<CustomCorridor> & Omit<CustomCorridor, 'id'>,
): Promise<number> {
    const { id, ...corridor } = customCorridor;
    const customCorridorSnakeCase = snakeCase(corridor);

    const query =
        id !== undefined
            ? supabase.from('custom_corridors').update(customCorridorSnakeCase).eq('id', id)
            : supabase.from('custom_corridors').upsert(customCorridorSnakeCase, {
                  onConflict: 'layout_id,swing_measured_parameter_id',
              });

    const { error, data } = await query.select('*').single();

    if (error) {
        throw error;
    }

    return data.layout_id;
}

export async function bulkUpsertCustomCorridor(
    supabase: SupabaseClient<Database>,
    customCorridors: CustomCorridorPayload[],
): Promise<number> {
    const customCorridorsSnakeCase = snakeCase(customCorridors, { deep: true });

    const { error } = await supabase.from('custom_corridors').upsert(customCorridorsSnakeCase, {
        onConflict: 'layout_id,swing_measured_parameter_id',
    });

    if (error) {
        throw error;
    }

    return customCorridors.length ? customCorridors[0].layoutId : -1;
}

export async function deleteCustomCorridor(
    supabase: SupabaseClient<Database>,
    customCorridorId: number,
): Promise<number> {
    const { data: corridor, error: selectError } = await supabase
        .from('custom_corridors')
        .select('layout_id')
        .eq('id', customCorridorId)
        .single();

    if (selectError) {
        throw selectError;
    }

    const { error } = await supabase.from('custom_corridors').delete().eq('id', customCorridorId);

    if (error) {
        throw error;
    }

    return corridor.layout_id;
}

export async function getAllCustomCorridorsByLayoutId(
    supabase: SupabaseClient<Database>,
    layoutId: number,
): Promise<CustomCorridor[]> {
    const { data, error } = await supabase.from('custom_corridors').select('*').eq('layout_id', layoutId);

    if (error) {
        throw error;
    }

    return camelCase(data, { deep: true }).map((corridor) => customCorridorSchema.parse(corridor));
}
